

































import Debug from 'debug'
import { Vue, Component, Prop, Emit } from 'vue-property-decorator';
import { resolve } from '@/app/container';
import { EventStream, EventStreamSymbol } from '@/app/event-stream/event-stream';
import {
  SectionInteractionEvent,
  SectionInteractedEventName
} from '@/app/statistics/events/section-interaction-events';
import { HOTSPOT_TYPE } from '@/app/statistics/handlers/hotspot-items-completion-handler';
import MarkerPlus from '@/app/presenter/components/marker-plus.vue';
import * as Api from "@/app/interaction/api";
import { HotspotState, HotspotItem, HotspotContent } from './hotspot-interfaces'
import { TranslateResult } from 'vue-i18n';
import i18n from '@/plugins/i18n';

interface InternalHotspotItem {
  x: number;
  y: number;
  content: string;
  touched: boolean;
}

const debug = Debug('hotspot-component')

@Component({
  components: {
    MarkerPlus
  }
})
export default class HotspotComponent extends Vue
  implements Api.InteractiveComponent<HotspotState, HotspotContent> {

  @Prop({
    required: true
  })
  public meta: Api.InteractionComponentMeta;

  @Prop({
    required: true
  })
  public content: HotspotContent;

  @Prop({
    required: true
  })
  public state: HotspotState | null;

  private internalItems: InternalHotspotItem[] = [];
  private selectedIndex = -1;

  private selectedMarker = {};
  private showMarkerPopup = false;

  private popupActiveById: { [id: string]: boolean };

  private readonly spotWidth: number = 45;
  private readonly spotHeight: number = 45;

  private widthPercent: number = 0;

  private imageOriginalWidth: number = 0;
  private imageOriginalHeight: number = 0;

  private readonly mainContainerSelector: string = '.main-container';
  private readonly clickNamespace: string = 'hotspot';

  private mounted(): void {

    this.setRatioValuesByImage();

    this.internalItems = this.content.items.map((item, index) => ({
      ...item,
      touched: this.state?.touched[index] || false,
    }));

    let activeIndex = -1
    if (this.state) {
      activeIndex = this.state.activeIndex;
      if (this.state.activeIndex >= 0) {
        this.showMarkerPopup = true
      }
    }

    this.popupActiveById = this.initialPopupState(activeIndex);
    this.selectedIndex = activeIndex

    $(() => {
      $(this.mainContainerSelector).on(`click.${this.clickNamespace}`, () => this.closePopup());
    });
  }

  private beforeDestroy(): void {
    $(this.mainContainerSelector).off(`click.${this.clickNamespace}`);
  }

  private createItemId(index: number): string {
    return `${this.meta.id}-${index}`;
  }

  private calculateStyle(x: number, y: number) {
    const halfSpotWidth: number = Math.round(this.spotWidth / 2);
    const halfSpotHeight: number = Math.round(this.spotHeight / 2);
    const xPercent: number = x * 100 / this.imageOriginalWidth;
    const yPercent: number = y * 100 / this.imageOriginalHeight;
    return {
      left: `calc(${xPercent}% - ${halfSpotWidth}px)`,
      top: `calc(${yPercent}% - ${halfSpotHeight}px)`
    };
  }

  private onPrevClicked(index: number): void {
    const isFirst: boolean = index === 0;
    if (isFirst) {
      return;
    }

    const prevIndex: number = index - 1;
    const prevMarker: HotspotItem = this.internalItems[prevIndex];
    const prevId: string = this.createItemId(prevIndex);
    const markerParentEl = this.$refs[`marker${prevIndex}`] as Vue;

    if (Array.isArray(markerParentEl) && markerParentEl[0] && markerParentEl[0].$el) {
      markerParentEl[0].$el.querySelector('a').focus();
    }

    this.toggleMarkerPopup(prevMarker, prevId, prevIndex);
  }

  private onNextClicked(index: number): void {
    const isLast: boolean = index === this.internalItems.length - 1;
    if (isLast) {
      return;
    }

    const nextIndex: number = index + 1;
    const nextMarker: HotspotItem = this.internalItems[nextIndex];
    const nextId: string = this.createItemId(nextIndex);
    const markerParentEl = this.$refs[`marker${nextIndex}`] as Vue;

    if (Array.isArray(markerParentEl) && markerParentEl[0] && markerParentEl[0].$el) {
      markerParentEl[0].$el.querySelector('a').focus();
    }

    this.toggleMarkerPopup(nextMarker, nextId, nextIndex);
  }

  private toggleMarkerPopup(marker: HotspotItem, itemId: string, index: number) {

    debug('toggleMarkerPopup', marker, itemId, index);

    debug('selectedIndex', this.selectedIndex, typeof this.selectedIndex, typeof index)

    if (this.selectedIndex === index && this.showMarkerPopup) {
      this.showMarkerPopup = false;
      this.selectedIndex = -1;
      this.popupActiveById = this.initialPopupState(this.selectedIndex);
    } else {
      this.selectedMarker = marker;
      this.selectedIndex = index;
      this.showMarkerPopup = true;

      // hide the others
      Object.entries(this.popupActiveById).forEach(([id, state]) => {
        this.popupActiveById[id] = id === itemId;
      });
    }

    let progressChanged = false

    if (!this.internalItems[index].touched) {
      this.internalItems[index].touched = true;
      progressChanged = true
    }

    this.emitInteractionStateChanged();
    if (progressChanged) {
      this.emitInteractionResultChanged();
    }
  }

  private closePopup(): void {

    if (this.selectedIndex < 0) {
      return
    }

    debug('closePopup');

    this.selectedIndex = -1;
    this.popupActiveById = this.initialPopupState(this.selectedIndex);
    this.showMarkerPopup = false

    this.emitInteractionStateChanged();
  }

  private initialPopupState(activeIndex: number): { [id: string]: boolean } {
    const state: { [itemId: string]: boolean } = {};
    let index: number = 0;
    this.internalItems.forEach((item) => {
      state[this.createItemId(index)] = index === activeIndex ? true : false;
      index++;
    });

    return state;
  }

  private setPopupPosition(x: number) {
    const xPercent: number = (x - (this.spotWidth / 2)) / this.widthPercent;

    if (xPercent > 50) {
      return 'left';
    } else {
      return 'right';
    }
  }

  private setRatioValuesByImage(): void {
    const image = new Image();
    image.src = this.content.imageSrc;
    image.onload = () => {
      this.imageOriginalWidth = image.width;
      this.imageOriginalHeight = image.height;

      const $image = $(`[src="${this.content.imageSrc}"].hotspot-image`);
      const imageVisible: boolean = $image.is(':visible');

      this.widthPercent = this.imageOriginalWidth / 100;
    };
  }

  @Emit('interaction-result-changed')
  public emitInteractionResultChanged() {
    let touchedItems = 0;

    for (let i in this.internalItems) {
      if (this.internalItems[i].touched) {
        touchedItems ++;
      }
    }

    const progress = parseFloat((touchedItems / this.internalItems.length).toFixed(2));

    const result: Api.Result = {
      progress,
      status: progress < 1 ? 'incomplete' : 'completed'
    }

    debug('emitInteractionResultChanged', result);

    return result
  }

  @Emit('interaction-state-changed')
  public emitInteractionStateChanged() {
    let activeIndex: number = -1;

    for (let i in this.popupActiveById) {
      if (this.popupActiveById[i]) {
        // REVIEW: why index stored as string?
        activeIndex = parseInt(i.split('-')[1]);
      }
    }

    const state = {
      touched: this.internalItems.map(item => item.touched || false),
      activeIndex,
    };

    debug('emitInteractionStateChanged', state);

    return state
  }

  private t(key: string, params: any = {}): TranslateResult {
    return i18n.t(key, params);
  }
}
